"use client";

import { type Call, SignalWire, WebRTCCall } from "@signalwire/js";
import type { Subscription } from "rxjs";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCallMute } from "@/components/call/use-call-mute";
import { useRemoteAudio } from "@/components/call/use-remote-audio";
import { useUser } from "@/contexts/user-provider";
import { cleanPhoneNumber } from "@/lib/format-data";
import { isNativeHost } from "@/lib/native-bridge";
import { applyPstnAudioCodecPreference } from "@/lib/signalwire";
import { toast } from "@/lib/use-toast";
import { createVoiceOnlyInboundWebRTCApiProvider } from "@/lib/voice-only-webrtc-provider";
import { PatientSummary } from "@/types/patient";
import { captureSoftphoneEvent, captureSoftphoneFailure } from "./softphone-telemetry";
import { SubscriberCredentialProvider, type SubscriberTokenResponse } from "./subscriber-credential-provider";
import { requestData } from "@/api/api-config";
import { fetchRingingCall, notifyRingGroupAnswered } from "@/api/provider/comms/call";
import { searchPatient } from "@/api/provider/user/patient/patient";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { isOnCall, useActiveCallStore } from "@/stores/active-call-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

// `connecting` covers the gap between the Answer click and SignalWire accepting
// the answer. Until that resolves nobody is on the line, so nothing may treat
// the call as active — not the UI, and not the backend's answered stamp.
type InboundCallState = "idle" | "ringing" | "connecting" | "active" | "ended" | "cancelled";
type CancelReason = "caller_hangup" | "no_answer" | null;
interface CallerInfo {
  callerName: string;
  callerNumber: string;
}
interface InboundCallContextType {
  callState: InboundCallState;
  cancelReason: CancelReason;
  callerInfo: CallerInfo | null;
  matchedPatient: PatientSummary | null;
  isPatientLookupLoading: boolean;
  /** Location of the number the caller dialed, once the ringing call is found. */
  dialedLocationName: string | null;
  answer: () => void;
  decline: () => void;
  hangup: () => void;
  toggleMute: () => void;
  isMuted: boolean;
  currentCall: Call | null;
  activeCallId: string | null;
  isTransferring: boolean;
  onTransferInitiated: () => void;
}
const InboundCallContext = createContext<InboundCallContextType | null>(null);
export function useInboundCall() {
  return useContext(InboundCallContext);
}

// How many busy-rejected call ids to remember, so a re-emitted
// incomingCalls$ array doesn't fire a duplicate "missed call" notice.
const BUSY_REJECT_HISTORY = 20;

/**
 * Reject a call that arrived while the user is already busy, and tell them
 * once that it happened.
 *
 * Rejecting (rather than silently swallowing the invite) is what makes the
 * caller fall through to the next ring group member or voicemail
 * immediately, instead of burning the full ring timeout on someone who
 * can't pick up.
 *
 * `notifiedIds` is mutated in place: incomingCalls$ re-emits the same array,
 * so the notice has to be deduped by call id.
 */
function rejectAsBusy(call: Call, notifiedIds: string[]) {
  try {
    call.reject();
  } catch {
    // The leg may already be gone — nothing to do.
  }
  if (notifiedIds.includes(call.id)) return;
  notifiedIds.push(call.id);
  if (notifiedIds.length > BUSY_REJECT_HISTORY) {
    notifiedIds.shift();
  }

  // Deliberately carries no caller identity: this is the one telemetry point
  // that has a name and number in hand, and neither belongs in analytics.
  captureSoftphoneEvent("softphone_incoming_rejected_busy");
  const rtcCall = call as WebRTCCall;
  const caller = rtcCall.fromName || rtcCall.from || "Someone";
  toast({
    title: "Missed call",
    description: `${caller} called while you were on another call.`,
    variant: "info"
  });
}

// How often to check that the live client is still connected and registered.
// The check only reads SDK state, so it is cheap. It never builds a client
// where there is none; that is the init ladder's and the backstop's job.
const HEALTH_CHECK_INTERVAL_MS = 60 * 1000;

// Backstop for a provider left with no client at all — e.g. a denied mic, which
// stops the init ladder. It used to double as the token rotation timer; token
// renewal is now the SDK's (see `SubscriberCredentialProvider`), and with a
// healthy client this tick is a no-op.
const RECOVERY_BACKSTOP_INTERVAL_MS = 50 * 60 * 1000;

// How long the client must stay disconnected or unregistered before it is
// rebuilt. The SDK reconnects a dropped WebSocket on its own; rebuilding the
// moment a check sees it down would tear down that reconnect and open exactly
// the connection-swap window that lost answers and dials. The first unhealthy
// sighting schedules its own re-check at this delay, so the rebuild is not left
// waiting on the next 60s health tick.
const UNHEALTHY_REBUILD_GRACE_MS = 15 * 1000;

// Clock-drift wake detector. macOS Chrome does not fire
// `visibilitychange` when the OS locks the screen on a foreground tab,
// and `online` stays quiet across short sleeps (WiFi association is
// preserved). The drift detector ticks every DRIFT_CHECK_INTERVAL_MS;
// if the wall-clock gap between two ticks exceeds DRIFT_WAKE_THRESHOLD_MS,
// JS timers were paused — which only happens on device sleep,
// hibernation, or browser-level freezing. The 3× ratio is generous
// enough to ignore routine CPU throttling (which rarely pushes timers
// past ~2×).
const DRIFT_CHECK_INTERVAL_MS = 10 * 1000;
const DRIFT_WAKE_THRESHOLD_MS = 30 * 1000;

// Bound on `client.register()` after a fresh `new SignalWire(...)`.
// Without this, a half-open WebSocket on wake can hang the await
// indefinitely, leaving `recoveryInFlightRef` true and gating out every
// subsequent wake event — the drift detector would become a no-op.
const REGISTER_TIMEOUT_MS = 10 * 1000;

// Bounded retry ladder for subscriber initialization. Full jitter over a
// doubling window — `Math.random() * min(cap, base * 2 ** attempt)` — copied
// from the proven ladder in `contexts/websocket-provider.tsx`.
//
// Jitter is not optional here. Every ring-group member's tab ticks
// its checks from roughly the same login time, so a shared
// outage releases the whole clinic's retries in lockstep without it.
const INIT_RETRY_BASE_MS = 2 * 1000;
const INIT_RETRY_CAP_MS = 60 * 1000;

// Floor between two wake-driven init attempts. Wake signals arrive in bursts
// (an unlock fires `online` and `visibilitychange` together, and the drift
// detector may tick alongside them), and a flapping network would otherwise
// turn them into a hot loop.
const WAKE_RETRY_MIN_INTERVAL_MS = 5 * 1000;

// `requestData` defaults to swallowing every failure into `null`, which is
// also what it returns for a legitimate empty 200 — indistinguishable at the
// call site, which is how 228 production failures reached a bare `return`.
// This call passes `catchError = false` so the real error survives.
//
// `refreshSubscriberToken` below deliberately keeps the swallowing default:
// `SubscriberCredentialProvider.refresh` falls back from it to this function,
// and that fallback reads a `null` return. Making it throw would skip the
// fallback and double the retry volume during exactly the outages that cause
// these failures. The SDK's refresh retry schedule wraps the whole refresh
// instead.
//
// The retry has to live here rather than in ky: ky's `retry` only applies to
// GET/PUT/HEAD/DELETE/OPTIONS/TRACE, so every POST — including both of these —
// is single-attempt today.
async function fetchSubscriberToken(): Promise<SubscriberTokenResponse | null> {
  return await requestData<SubscriberTokenResponse>("comms/call/subscriber-token", "post", undefined, false);
}

// getUserMedia rejection names that mean "the user or the platform refused, and
// asking again changes nothing". Everything else it can throw — NotReadableError
// (device busy in another app), NotFoundError (no device attached yet),
// AbortError, OverconstrainedError — is transient and must stay retryable.
const PERMANENT_MIC_ERROR_NAMES = new Set(["NotAllowedError", "SecurityError"]);

// Whether the browser already holds a microphone grant for this origin *and*
// a microphone is attached. A grant alone only means no prompt is needed —
// with no input device, capture still fails, and the getUserMedia path below
// is what turns that into a NotFoundError retry instead of registering a
// subscriber that cannot answer. Any failure — no Permissions API, or a
// browser that rejects the "microphone" name — answers false, so the caller
// falls back to asking via getUserMedia.
//
// A hidden tab trusts the grant alone: the Media Capture spec lets
// `enumerateDevices` wait until the document is visible, and a background tab
// rebuilding after a network drop must register without being foregrounded.
async function isMicReadyWithoutPrompt(): Promise<boolean> {
  if (!navigator.permissions?.query) return false;
  try {
    const status = await navigator.permissions.query({
      name: "microphone" as PermissionName
    });
    if (status.state !== "granted") return false;
    if (document.visibilityState !== "visible") return true;
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === "audioinput");
  } catch {
    return false;
  }
}

// A microphone the user has actively denied is not a transient fault — retrying
// re-prompts nothing and re-toasts on every wake. Distinguish it from the
// network faults so the ladder can skip it.
class MicrophoneDeniedError extends Error {
  constructor(cause: unknown) {
    super("Microphone permission denied");
    this.name = "MicrophoneDeniedError";
    this.cause = cause;
  }
}
async function refreshSubscriberToken(refreshToken: string): Promise<SubscriberTokenResponse | null> {
  return await requestData<SubscriberTokenResponse>("comms/call/subscriber-token/refresh", "post", {
    json: {
      refresh_token: refreshToken
    }
  });
}

// Workaround for a SignalWire JS SDK bug in inbound calls.
//
// The SDK calls addTransceiver(micTrack) before setRemoteDescription(offer).
// When the browser processes the offer, it creates a SECOND audio transceiver
// to match the offer's m-line instead of reusing the existing one. The SDP
// answer is generated from this second transceiver, which has no sender track,
// producing "a=recvonly" — so the browser never sends audio to the caller.
//
// Fix: patch RTCPeerConnection.prototype.createAnswer to move the mic track
// to the offer-matched transceiver and set direction to "sendrecv" before
// the SDP answer is generated.
let createAnswerPatched = false;
function patchCreateAnswerForInboundAudio() {
  if (createAnswerPatched || typeof RTCPeerConnection === "undefined") return;
  createAnswerPatched = true;
  const origCreateAnswer = RTCPeerConnection.prototype.createAnswer;
  (RTCPeerConnection.prototype as unknown as Record<string, unknown>).createAnswer = async function (this: RTCPeerConnection, ...args: [RTCAnswerOptions?]) {
    const transceivers = this.getTransceivers();

    // Find the audio transceiver that has the mic track on its sender
    // and the one matched to the offer's m-line that has no sender track.
    let audioTrack: MediaStreamTrack | null = null;
    let tracklessAudioTransceiver: RTCRtpTransceiver | null = null;
    for (const t of transceivers) {
      if (t.sender.track?.kind === "audio") {
        audioTrack = t.sender.track;
      }
      if (!t.sender.track && t.receiver.track?.kind === "audio") {
        tracklessAudioTransceiver = t;
      }
    }

    // Move the mic track to the offer-matched transceiver so the SDP
    // answer includes it and advertises "sendrecv".
    if (tracklessAudioTransceiver && audioTrack) {
      await tracklessAudioTransceiver.sender.replaceTrack(audioTrack);
    }

    // Ensure all audio transceivers are sendrecv
    for (const t of transceivers) {
      const kind = t.sender.track?.kind ?? t.receiver.track?.kind;
      if (kind === "audio") {
        t.direction = "sendrecv";
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (origCreateAnswer as any).apply(this, args);
  };
}

// Upper bound on waiting for an answered call's audio to come up. The SDK
// reports a timed-out answer RPC on `errors$` after 5s; this covers the cases
// it never reports, such as an accepted answer whose media never connects.
const ANSWER_CONNECT_TIMEOUT_MS = 15_000;
const ANSWER_FAILED_STATUSES = new Set(["disconnecting", "disconnected", "failed", "destroyed"]);

/**
 * Resolve once an answered call is really connected; reject if it never gets
 * there.
 *
 * `call.answer()` is synchronous — it only flags the call as accepted, and the
 * SDK sends the answer to SignalWire in the background. Its outcome surfaces
 * on the call's streams: `status$` reaches `connected` when the audio path is
 * up, and a failed answer (e.g. the 5s answer RPC timing out on a slow link)
 * is pushed to `errors$` as a non-fatal error that leaves the call alive but
 * never connected.
 */
function waitForAnsweredCallToConnect(call: Call): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const subscriptions: Subscription[] = [];
    const settle = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      subscriptions.forEach(s => s.unsubscribe());
      if (error) reject(error);else resolve();
    };
    const timer = setTimeout(() => {
      settle(Object.assign(new Error(`Call did not connect within ${ANSWER_CONNECT_TIMEOUT_MS}ms of answering`), {
        name: "AnswerConnectTimeout"
      }));
    }, ANSWER_CONNECT_TIMEOUT_MS);
    const callEnded = () => settle(Object.assign(new Error("Call ended before it connected"), {
      name: "AnswerCallEnded"
    }));

    // Either stream can settle synchronously on subscribe, before its
    // subscription is recorded above; drop it straight away in that case.
    const track = (subscription: Subscription) => {
      if (settled) subscription.unsubscribe();else subscriptions.push(subscription);
    };
    track(call.status$.subscribe({
      next: status => {
        if (status === "connected") settle();else if (ANSWER_FAILED_STATUSES.has(status)) callEnded();
      },
      // The SDK completes its subjects on destroy() without a final status.
      complete: callEnded
    }));
    track(call.errors$.subscribe(callError => {
      // Media hiccups (a device change, a muted track) do not stop the call
      // connecting; anything else during the answer means it will not.
      if (callError.fatal || callError.kind !== "media") {
        settle(callError.error);
      }
    }));
  });
}
export const InboundCallProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    currentUser
  } = useUser();
  const [callState, setCallState] = useState<InboundCallState>("idle");
  const [callerInfo, setCallerInfo] = useState<CallerInfo | null>(null);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [matchedPatient, setMatchedPatient] = useState<PatientSummary | null>(null);
  const [isPatientLookupLoading, setIsPatientLookupLoading] = useState(false);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [cancelReason, setCancelReason] = useState<CancelReason>(null);
  const openSidebar = useRightSidebarStore(state => state.open);
  const beginActiveCall = useActiveCallStore(s => s.beginCall);
  const endActiveCall = useActiveCallStore(s_0 => s_0.endCall);
  const clientRef = useRef<SignalWire | null>(null);
  const matchedPatientRef = useRef<PatientSummary | null>(null);
  const healthIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // When a health check first saw the client disconnected or unregistered;
  // null while it is healthy. See `UNHEALTHY_REBUILD_GRACE_MS`.
  const unhealthySinceRef = useRef<number | null>(null);
  const graceRecheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCallRef = useRef<Call | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  const busyRejectedIdsRef = useRef<string[]>([]);
  const initializedRef = useRef(false);
  const incomingCallSubRef = useRef<Subscription | null>(null);
  const callStatusSubRef = useRef<Subscription | null>(null);
  const errorSubRef = useRef<Subscription | null>(null);
  const beforeUnloadRef = useRef<(() => void) | null>(null);
  const pageHideRef = useRef<((event: PageTransitionEvent) => void) | null>(null);
  const driftIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastDriftTickAtRef = useRef(0);
  const initializeSubscriberRef = useRef<(() => Promise<"ok" | "stale">) | null>(null);
  const recoveryInFlightRef = useRef(false);
  // Retry ladder state. `initRetryTimerRef` doubles as the in-flight lock: the
  // timer callback nulls it *before* awaiting, so "no timer pending" and "an
  // attempt is running" are the same observable condition.
  const initRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initAttemptRef = useRef(0);
  const micDeniedRef = useRef(false);
  // True while an init attempt is awaiting. Distinct from `initRetryTimerRef`,
  // which is null during the attempt, and from `initializedRef`, which is only
  // set once `register()` has resolved.
  const initInFlightRef = useRef(false);
  // Set when this provider instance is torn down, so callbacks that resume
  // after an await can tell they have outlived it.
  const disposedRef = useRef(false);
  const lastWakeRetryAtRef = useRef(0);
  // Bumped by every client teardown. An in-flight `initializeSubscriber`
  // compares against the value it captured at entry to tell whether the
  // provider it belongs to is still the live one.
  const generationRef = useRef(0);
  // Breaks the definition cycle between `attemptInit` and `scheduleInitRetry`,
  // which reference each other.
  const scheduleInitRetryRef = useRef<(() => void) | null>(null);
  const attemptInitRef = useRef<(() => Promise<void>) | null>(null);
  // `handleCallEnded` is defined before `recoverConnection`; this breaks the
  // ordering cycle the same way `attemptInitRef` does.
  const recoverConnectionRef = useRef<((trigger: "interval" | "wake") => Promise<void>) | null>(null);
  const {
    isMuted,
    toggleMute,
    resetMute
  } = useCallMute({
    call: currentCall
  });
  useRemoteAudio(currentCall);

  // --- cleanup helpers (match swdialer's disconnect/handleCallEnded) ---

  const cleanupCallSubscriptions = useCallback(() => {
    callStatusSubRef.current?.unsubscribe();
    callStatusSubRef.current = null;
  }, []);

  // --- shared incoming call handler ---

  const handleIncomingCalls = useCallback((calls: Call[]) => {
    // Every ringing leg, not just the first: the SDK can emit a new invite
    // alongside the one we're already tracking, and picking only the first
    // would leave the newcomer unanswered until it timed out.
    const ringingCalls = calls.filter(c => c.status === "ringing" || c.status === "new");
    if (ringingCalls.length === 0) return;

    // Already busy — don't ring over the call the user is on.
    // `currentCallRef` is this provider's own answered call and
    // `pendingCallRef` a different call already ringing here; `isOnCall()`
    // covers the outbound dialer, which runs its own SignalWire client and
    // would otherwise be invisible from here.
    if (currentCallRef.current || pendingCallRef.current || isOnCall()) {
      // Reject every invite except our own pending leg, which the SDK
      // re-emits on each tick and which we're already showing.
      for (const call of ringingCalls) {
        if (call.id !== pendingCallRef.current?.id) {
          rejectAsBusy(call, busyRejectedIdsRef.current);
        }
      }
      return;
    }

    // Free: take the first invite and reject any that arrived with it.
    const [ringingCall, ...extraCalls] = ringingCalls;
    for (const call_0 of extraCalls) {
      rejectAsBusy(call_0, busyRejectedIdsRef.current);
    }
    pendingCallRef.current = ringingCall;
    const rtcCall = ringingCall as WebRTCCall;
    setCallerInfo({
      callerName: rtcCall.fromName ?? "",
      callerNumber: rtcCall.from ?? ""
    });
    setCallState("ringing");

    // Track whether the caller explicitly hung up (signaling "ending" event
    // with end_reason) vs a ring timeout (no such event before disconnect).
    let callerHungUp = false;
    const cleanupCanceled = () => {
      if (pendingCallRef.current?.id === ringingCall.id) {
        pendingCallRef.current = null;
        setCancelReason(callerHungUp ? "caller_hangup" : "no_answer");
        setCallState("cancelled");
        setTimeout(() => {
          setCallState("idle");
          setCancelReason(null);
          setCallerInfo(null);
          setMatchedPatient(null);
          matchedPatientRef.current = null;
        }, 2000);
      }
      sigSub.unsubscribe();
      pendingSub.unsubscribe();
    };
    const sigSub = rtcCall.signalingEvent$.subscribe(evt => {
      const event = evt as Record<string, unknown>;
      if (event.call_state === "ending" && event.end_reason) {
        callerHungUp = true;
      }
    });
    const pendingSub = ringingCall.status$.subscribe({
      next: status => {
        if (status === "disconnected" || status === "failed" || status === "destroyed") {
          cleanupCanceled();
        }
      },
      // The SDK completes all subjects on destroy() without emitting a
      // terminal status, so we must also handle completion.
      complete: () => {
        if (pendingCallRef.current?.id === ringingCall.id) {
          cleanupCanceled();
        }
      }
    });
  }, []);
  const handleCallEnded = useCallback(() => {
    cleanupCallSubscriptions();
    if (currentCallRef.current) {
      endActiveCall(currentCallRef.current.id);
    }
    setCallState("ended");
    setCurrentCall(null);
    currentCallRef.current = null;
    setActiveCallId(null);
    setIsTransferring(false);
    resetMute();
    setTimeout(() => {
      setCallState("idle");
      setCallerInfo(null);
      setMatchedPatient(null);
      matchedPatientRef.current = null;
    }, 1500);

    // Health checks never rebuild on top of a call. If the client dropped
    // while this one was up, pick the recovery back up now that the line is
    // free: this starts the grace clock, and its re-check rebuilds.
    const client = clientRef.current;
    if (!client || !client.isConnected || !client.isRegistered) {
      void recoverConnectionRef.current?.("wake");
    }
  }, [resetMute, cleanupCallSubscriptions]);

  // Tear down the SignalWire client (unregister + disconnect).
  // Only safe to call when the client is fully registered.
  //
  // Also clears `initializedRef` so the provider can be rebuilt: iOS
  // fires `pagehide` with `persisted === false` on lock/background
  // (an open WebSocket makes the page bfcache-ineligible), which runs
  // this. If `initializedRef` stayed true, `initializeSubscriber`
  // would short-circuit and the only recovery would be a page refresh.
  const teardownClient = useCallback(() => {
    // Any in-flight `initializeSubscriber` no longer owns the field.
    generationRef.current += 1;
    if (clientRef.current) {
      clientRef.current.unregister().catch((error: unknown) => {
        console.debug("[InboundCall] Unregister error (ignored):", error);
      });
      clientRef.current.disconnect().catch((error_0: unknown) => {
        console.debug("[InboundCall] Disconnect error (ignored):", error_0);
      });
      clientRef.current = null;
    }
    initializedRef.current = false;
  }, []);

  // Tears down the *client* and per-call state. Deliberately does NOT touch the
  // wake listeners or the drift/health intervals: those live in their own
  // effect (see "recovery lifecycle" below) and must outlive every rebuild.
  //
  // `recoverConnection` calls this immediately before re-running
  // `initializeSubscriber`. When the listeners were torn down here as well, that
  // was survivable only because init re-registered them on its way out — so any
  // init that failed before reaching that point removed its own means of ever
  // being retried. That is the defect this split exists to prevent; do not move
  // listener removal back into this function.
  const cleanup = useCallback(() => {
    incomingCallSubRef.current?.unsubscribe();
    incomingCallSubRef.current = null;
    errorSubRef.current?.unsubscribe();
    errorSubRef.current = null;
    cleanupCallSubscriptions();

    // Remove beforeunload listener
    if (beforeUnloadRef.current) {
      window.removeEventListener("beforeunload", beforeUnloadRef.current);
      beforeUnloadRef.current = null;
    }
    if (pageHideRef.current) {
      window.removeEventListener("pagehide", pageHideRef.current);
      pageHideRef.current = null;
    }
    unhealthySinceRef.current = null;
    if (graceRecheckTimerRef.current) {
      clearTimeout(graceRecheckTimerRef.current);
      graceRecheckTimerRef.current = null;
    }

    // Tear down the SignalWire client so the provider goes offline
    teardownClient();
    if (currentCallRef.current) {
      endActiveCall(currentCallRef.current.id);
    }
    pendingCallRef.current = null;
    currentCallRef.current = null;
    setCurrentCall(null);
    setCallState("idle");
    setCallerInfo(null);
    setMatchedPatient(null);
    matchedPatientRef.current = null;
    setActiveCallId(null);
    setIsTransferring(false);
    resetMute();
    initializedRef.current = false;
  }, [resetMute, cleanupCallSubscriptions, teardownClient, endActiveCall]);

  // Shared recovery path for the periodic health check and the wake listeners
  // (visibilitychange, online, drift).
  //
  // A live client is kept, not replaced. Token renewal happens inside the SDK
  // on the same connection (`SubscriberCredentialProvider.refresh`), and the
  // SDK reconnects a dropped WebSocket on its own. Swapping in a new client —
  // which this used to do on every wake and every 50 minutes — is what lost
  // answers and dials: the request went out on, or was answered over, the
  // socket being closed. So a client is rebuilt only when it is genuinely
  // dead: disconnected or unregistered, still so after
  // `UNHEALTHY_REBUILD_GRACE_MS`, and with no call ringing or up.
  //
  // With no client at all — iOS tears it down on every lock via `pagehide`
  // (persisted=false), and a failed init leaves none — the only path back is a
  // full rebuild: `cleanup()` drops the stale state, then the retry ladder
  // re-runs the whole setup (what a manual page refresh does, minus the
  // refresh). The rebuild goes through `attemptInit`, not
  // `initializeSubscriber` directly, so a rebuild that itself fails schedules
  // the next attempt instead of logging and stopping.
  const recoverConnection = useCallback(async (trigger: "interval" | "wake") => {
    const client_0 = clientRef.current;
    if (client_0) {
      if (client_0.isConnected && client_0.isRegistered) {
        unhealthySinceRef.current = null;
        return;
      }
      // A call ringing or up is evidence enough that the line works, and a
      // rebuild would drop it. Revisit on the next check.
      if (pendingCallRef.current || currentCallRef.current) return;
      const now = Date.now();
      if (unhealthySinceRef.current === null) {
        unhealthySinceRef.current = now;
        if (graceRecheckTimerRef.current) {
          clearTimeout(graceRecheckTimerRef.current);
        }
        graceRecheckTimerRef.current = setTimeout(() => {
          graceRecheckTimerRef.current = null;
          void recoverConnectionRef.current?.("interval");
        }, UNHEALTHY_REBUILD_GRACE_MS);
        return;
      }
      if (now - unhealthySinceRef.current < UNHEALTHY_REBUILD_GRACE_MS) {
        return;
      }
    }
    if (recoveryInFlightRef.current) return;
    // An init attempt is already running. Falling through would call
    // `cleanup()` — tearing down the client that attempt is in the middle of
    // building — and then bail out of `attemptInit`'s own in-flight guard,
    // leaving nothing registered.
    if (initInFlightRef.current) return;
    recoveryInFlightRef.current = true;
    captureSoftphoneEvent("softphone_recovery_attempt", {
      trigger,
      reason: client_0 ? "unhealthy" : "no_client"
    });
    try {
      cleanup();
      await attemptInitRef.current?.();
    } catch (error_1) {
      console.error(`[InboundCall] Connection rebuild failed (${trigger}):`, error_1);
    } finally {
      recoveryInFlightRef.current = false;
    }
  }, [cleanup]);

  // --- dialed location lookup ---

  // Staff in several locations' ring groups can't tell from the caller alone
  // which location is being called; the Call row created for this ring carries
  // the dialed number's location.
  const [dialedLocationName, setDialedLocationName] = useState<string | null>(null);
  useEffect(() => {
    if (callState !== "ringing") {
      if (callState === "idle") setDialedLocationName(null);
      return;
    }
    let cancelled = false;
    fetchRingingCall().then(ringing => {
      if (!cancelled) setDialedLocationName(ringing?.locationName ?? null);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [callState]);

  // --- patient lookup ---

  useEffect(() => {
    const callerNumber = callerInfo?.callerNumber;
    if (!callerNumber) {
      setMatchedPatient(null);
      matchedPatientRef.current = null;
      setIsPatientLookupLoading(false);
      return;
    }
    const digits = cleanPhoneNumber(callerNumber);
    if (digits.length < 10) {
      setMatchedPatient(null);
      matchedPatientRef.current = null;
      setIsPatientLookupLoading(false);
      return;
    }
    const abortController = new AbortController();
    setIsPatientLookupLoading(true);
    searchPatient(digits, abortController).then(results => {
      if (abortController.signal.aborted) return;
      const patient = results?.[0] ?? null;
      setMatchedPatient(patient);
      matchedPatientRef.current = patient;
    }).catch(() => {
      if (abortController.signal.aborted) return;
      setMatchedPatient(null);
      matchedPatientRef.current = null;
    }).finally(() => {
      if (!abortController.signal.aborted) {
        setIsPatientLookupLoading(false);
      }
    });
    return () => {
      abortController.abort();
    };
  }, [callerInfo?.callerNumber]);

  // --- initialize (matches swdialer's initialize) ---

  // Resolves "stale" when a teardown superseded this attempt part-way through,
  // so the caller can tell that apart from a completed registration. They are
  // not the same: a superseded attempt left nothing registered.
  const initializeSubscriber = useCallback(async (): Promise<"ok" | "stale"> => {
    if (initializedRef.current) return "ok";

    // An attempt spans several awaits and can run for ~30s. The provider can be
    // torn down inside any of them — `cleanup()` runs, but this function keeps
    // going and would then register a client and install listeners belonging to
    // a provider that no longer exists: a zombie subscriber registration that
    // SignalWire keeps routing INVITEs to, pulling calls away from the live
    // instance. `generationRef` is bumped by every teardown, so comparing
    // against the value captured here tells us whether we still own the field.
    const generation = generationRef.current;
    const isStale = () => generationRef.current !== generation;
    const startedAt = Date.now();
    const attempt = initAttemptRef.current;
    try {
      // Pre-request mic permission so the SDK can access audio for inbound calls.
      // The SDK calls getUserMedia when the call invite arrives (before user gesture),
      // so permission must already be granted.
      //
      // Skipped when the browser already reports it granted and a mic is
      // attached. This runs on every load and every rebuild, and opening the
      // mic — even for an instant — switches a Bluetooth headset into its call
      // profile and triggers OS "communications" ducking, cutting the audio of
      // a Zoom call the user is already on.
      try {
        const micReady = await isMicReadyWithoutPrompt();
        // A teardown during that check must not go on to open the mic, or
        // prompt, for a subscriber that will be thrown away.
        if (isStale()) return "stale";
        if (!micReady) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });
          stream.getTracks().forEach(track => track.stop());
        }
      } catch (error_3) {
        // Only an actual refusal is terminal. `NotReadableError` (another app
        // holds the mic — a video call, say) and `NotFoundError` (no device
        // yet) are transient: marking them terminal would silently drop the
        // user out of the ring group for the rest of the session, which is the
        // exact bug this provider is being fixed for. Let the ladder retry
        // those, so finishing the other call or plugging in a headset restores
        // inbound calling with no reload.
        const name = (error_3 as Error | undefined)?.name;
        if (!PERMANENT_MIC_ERROR_NAMES.has(name ?? "")) {
          console.error("[InboundCall] Microphone unavailable:", error_3);
          captureSoftphoneFailure("softphone_mic_unavailable", {
            reason: name ?? "unknown",
            attempt
          });
          throw error_3;
        }
        console.error("[InboundCall] Microphone permission denied:", error_3);
        captureSoftphoneFailure("softphone_mic_denied", {
          reason: name ?? "unknown",
          attempt
        });
        // Toast once per mount. The wake listeners now survive a failed init,
        // so without this latch a permanently-denied mic re-toasts on every
        // visibilitychange and every drift tick.
        if (!micDeniedRef.current) {
          micDeniedRef.current = true;
          toast({
            title: "Microphone Required",
            description: "Please enable microphone access to receive calls.",
            variant: "warning"
          });
        }
        throw new MicrophoneDeniedError(error_3);
      }
      micDeniedRef.current = false;
      if (isStale()) return "stale";
      const tokenResponse = await fetchSubscriberToken();
      // `null` here is a legitimate empty 200 — a real failure would have
      // thrown, because `fetchSubscriberToken` passes `catchError = false`.
      // Either way there is no token to register with, so surface it to the
      // retry ladder rather than returning silently.
      if (!tokenResponse?.token) {
        captureSoftphoneFailure("softphone_token_missing", {
          attempt
        });
        throw new Error("Subscriber token response contained no token");
      }
      if (isStale()) return "stale";

      // Patch RTCPeerConnection.createAnswer to fix inbound call audio
      patchCreateAnswerForInboundAudio();

      // One client for the life of this session. The credential provider lets
      // the SDK renew the token on this client's own connection, so it is never
      // replaced just to stay authenticated.
      const credentialProvider = new SubscriberCredentialProvider(tokenResponse, {
        fetchToken: fetchSubscriberToken,
        refreshToken: refreshSubscriberToken,
        onRefreshFailed: error_4 => {
          console.error("[InboundCall] Token refresh failed:", error_4);
          captureSoftphoneFailure("softphone_token_refresh_failed", {
            reason: (error_4 as Error | undefined)?.name ?? "unknown"
          });
        }
      });
      const client_1 = new SignalWire(credentialProvider, {
        webRTCApiProvider: createVoiceOnlyInboundWebRTCApiProvider()
      });
      applyPstnAudioCodecPreference(client_1);
      clientRef.current = client_1;

      // Subscribe to errors (matches swdialer)
      const errorSub = client_1.errors$.subscribe(error_5 => {
        console.error("[InboundCall] SignalWire error:", error_5);
      });
      errorSubRef.current = errorSub;

      // Register for incoming calls (matches swdialer: await client.register()).
      //
      // Bounded: a half-open WebSocket makes this await hang forever, and an unbounded hang here
      // pins `recoveryInFlightRef` true, which gates out every subsequent wake.
      let registerTimer: ReturnType<typeof setTimeout> | undefined;
      let registerTimedOut = false;
      try {
        await Promise.race([client_1.register(), new Promise<never>((_, reject) => {
          registerTimer = setTimeout(() => {
            registerTimedOut = true;
            reject(new Error("register() timed out"));
          }, REGISTER_TIMEOUT_MS);
        })]);
      } catch (error_6) {
        // A half-open WebSocket times out here rather than throwing, and the
        // two want telling apart: a timeout points at the connection, a throw
        // at the credential or the SDK.
        if (registerTimedOut) {
          captureSoftphoneFailure("softphone_register_timeout", {
            reason: "timeout",
            attempt,
            timeout_ms: REGISTER_TIMEOUT_MS
          });
        } else {
          captureSoftphoneFailure("softphone_register_failed", {
            reason: (error_6 as Error | undefined)?.name ?? "unknown",
            attempt
          });
        }
        throw error_6;
      } finally {
        if (registerTimer) clearTimeout(registerTimer);
      }

      // Past this point the client is registered with SignalWire and the server
      // will route INVITEs to it, so a later failure must fully tear it down
      // rather than merely drop the reference — a zombie registration keeps
      // stealing calls away from the ring group's failover assistant, which is
      // the exact symptom this provider is being fixed for.
      //
      // That applies to being torn down mid-register too. `teardownClient` ran
      // while we were awaiting, so it saw a `clientRef` that did not yet hold
      // this client and could not unregister it. Nothing else will, so do it
      // here rather than adopting a client the provider no longer wants.
      if (isStale()) {
        client_1.unregister().catch(() => {});
        client_1.disconnect().catch(() => {});
        errorSub.unsubscribe();
        return "stale";
      }
      initializedRef.current = true;

      // The one event that answers "is this person's line actually open?".
      // Its absence for a signed-in ring-group member is the alarm; `attempt`
      // and `duration_ms` show whether it took a ladder of retries to get here.
      captureSoftphoneEvent("softphone_registered", {
        attempt,
        duration_ms: Date.now() - startedAt
      });

      // Subscribe to incoming calls (matches swdialer's incomingCalls$ pattern)
      incomingCallSubRef.current = client_1.session.incomingCalls$.subscribe(handleIncomingCalls);

      // Register the beforeunload listener so the client tears down on page unload
      const handleBeforeUnload = () => {
        teardownClient();
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      beforeUnloadRef.current = handleBeforeUnload;

      // iOS Safari rarely fires `beforeunload` — tab close via the tab
      // manager, app-switcher swipe, and memory eviction all skip it.
      // `pagehide` is Apple's reliable counterpart; without it we leak
      // SignalWire sessions that linger as zombies and misroute INVITEs
      // away from this device on future reconnects.
      //
      // On iOS this also fires (with `persisted === false`, since an
      // open WebSocket makes the page bfcache-ineligible) on every
      // lock/background — not just real closes, which are
      // indistinguishable here. That's acceptable because the wake
      // handlers route through `recoverConnection`, which rebuilds from
      // scratch when `clientRef.current` is null. Skip teardown only
      // for `persisted === true` (true bfcache): the page is restored
      // intact via `pageshow` with React state preserved, so tearing
      // down would be wasted work.
      //
      // Tear down even with a call in progress. A guard that skipped
      // teardown mid-call was considered (to avoid dropping a call on a
      // brief lock) but rejected: on iOS a backgrounded Safari tab
      // can't keep WebRTC audio alive, so the call is already lost on
      // lock — and skipping the `unregister()` would (a) reintroduce
      // the very zombie registration this teardown prevents, until the
      // server's connection timeout, and (b) wedge recovery, since
      // `recoverConnection` never rebuilds while `currentCallRef` is set, so a
      // call that died silently during suspension would block every
      // subsequent wake rebuild until a manual hangup/refresh. Clean
      // teardown + `recoverConnection` rebuilding on unlock is the
      // strictly better outcome here.
      const handlePageHide = (event_0: PageTransitionEvent) => {
        if (event_0.persisted) return;
        teardownClient();
      };
      window.addEventListener("pagehide", handlePageHide);
      pageHideRef.current = handlePageHide;

      // The wake listeners and the drift / health-check intervals used to be
      // registered here. They now live in their own effect so that they exist
      // regardless of whether this function succeeds — see "recovery lifecycle".
      return "ok";
    } catch (error_2) {
      console.error("[InboundCall] Failed to initialize subscriber:", error_2);

      // Leave nothing half-built. `clientRef` in particular: left pointing at an
      // unregistered client, `recoverConnection` would treat it as a live
      // client and wait out the unhealthy grace period before rebuilding,
      // instead of rebuilding straight away.
      //
      // `teardownClient` unregisters as well as disconnects, which matters when
      // we failed *after* a successful register (e.g. subscribing to
      // `incomingCalls$` threw): SignalWire would otherwise keep routing INVITEs
      // to a client nothing is listening on.
      teardownClient();
      errorSubRef.current?.unsubscribe();
      errorSubRef.current = null;
      incomingCallSubRef.current?.unsubscribe();
      incomingCallSubRef.current = null;
      initializedRef.current = false;
      throw error_2;
    }
  }, [teardownClient, handleIncomingCalls]);

  // Keep a ref to the latest initializeSubscriber so recoverConnection
  // can rebuild from scratch without a definition cycle (recoverConnection is
  // defined before initializeSubscriber).
  useEffect(() => {
    initializeSubscriberRef.current = initializeSubscriber;
  }, [initializeSubscriber]);

  // --- retry ladder -------------------------------------------------------
  //
  // Every failure to bring the subscriber up schedules the next attempt.
  // There is no give-up branch: 228 real failures over nine days were
  // overwhelmingly one-shot (58% were more than 70 minutes from any other
  // failure by the same user), so the next attempt is very likely to succeed.
  // What made them fatal was that no next attempt existed.
  const attemptInit = useCallback(async () => {
    if (disposedRef.current) return;
    // Inside the mobile app the native side owns calling: its own SignalWire
    // client rings through CallKit. Starting this one too would register a
    // second subscriber for the same user, and its mic pre-grant opens web
    // audio capture on the app's shared audio session, which deadlocked
    // CoreAudio (SIGABRT) as soon as the native engine set up call audio.
    if (isNativeHost()) return;
    // An attempt can take ~30s (a 20s ky timeout on the token POST plus a 10s
    // register bound). `initRetryTimerRef` is null for that whole window
    // because the timer callback nulls it before awaiting, so it cannot serve
    // as the in-flight lock the way it does in the websocket provider — there,
    // an attempt is a synchronous `setSocketUrl`. Without this explicit flag a
    // wake event lands mid-attempt and starts a second `initializeSubscriber`,
    // and both can reach `client.register()`: two registered subscribers for
    // one user, one of them orphaned.
    if (initInFlightRef.current) return;
    initInFlightRef.current = true;
    try {
      const outcome = await initializeSubscriberRef.current?.();
      if (outcome === "stale") {
        // Superseded by a teardown, so nothing got registered — do not report
        // success and reset the ladder.
        //
        // Defensive rather than a fix for a reachable bug: today every teardown
        // that can land mid-attempt either disposes the provider (where
        // `scheduleInitRetry` correctly no-ops on `disposedRef`) or runs
        // `cleanup()` and calls `attemptInit` itself. The trap is that adding a
        // teardown path which arms no follow-up — `pagehide` would be one, were
        // it not registered after `register()` — would silently strand the
        // subscriber. Distinguishing the two outcomes costs nothing and closes
        // that off.
        scheduleInitRetryRef.current?.();
        return;
      }
      // Succeeded — reset the ladder, exactly as the websocket provider resets
      // its retry count in `onOpen` and nowhere else.
      initAttemptRef.current = 0;
    } catch (error_7) {
      if (error_7 instanceof MicrophoneDeniedError) {
        // Not transient. Retrying cannot grant a permission the user withheld;
        // a wake event will re-attempt if they change it.
        return;
      }
      scheduleInitRetryRef.current?.();
    } finally {
      initInFlightRef.current = false;
    }
  }, []);
  const scheduleInitRetry = useCallback(() => {
    // The provider may have been torn down while the attempt that scheduled us
    // was in flight — `attemptInit`'s catch runs after its await, potentially
    // after unmount. Re-arming there would leave a timer nothing can ever
    // clear, looping `getUserMedia` and token POSTs for the life of the tab.
    if (disposedRef.current) return;
    if (initRetryTimerRef.current) clearTimeout(initRetryTimerRef.current);
    const attempt_0 = initAttemptRef.current;
    initAttemptRef.current = attempt_0 + 1;
    // Full jitter: random across the whole window, not a band around it.
    const delay = Math.random() * Math.min(INIT_RETRY_CAP_MS, INIT_RETRY_BASE_MS * 2 ** attempt_0);
    initRetryTimerRef.current = setTimeout(() => {
      initRetryTimerRef.current = null;
      void attemptInit();
    }, delay);
  }, [attemptInit]);
  useEffect(() => {
    scheduleInitRetryRef.current = scheduleInitRetry;
    attemptInitRef.current = attemptInit;
  }, [scheduleInitRetry, attemptInit]);
  useEffect(() => {
    recoverConnectionRef.current = recoverConnection;
  }, [recoverConnection]);

  // --- recovery lifecycle -------------------------------------------------
  //
  // These listeners and intervals deliberately live outside
  // `initializeSubscriber`. Registered inside it — after `client.register()` —
  // any failure earlier in that function left the provider with no listeners
  // and no intervals, so `recoverConnection` was never called again and the
  // softphone stayed silently offline until the page was reloaded. Staff kept
  // working, believing their line was open, while calls fell through to the
  // ring group's AI failover assistant.
  //
  // `cleanup()` must not tear these down; see the note on that function.
  useEffect(() => {
    if (!currentUser?.isRingGroupMember) return;
    disposedRef.current = false;
    const onWake = () => {
      if (!navigator.onLine) return;
      // An attempt is mid-flight — let it finish rather than stacking a second
      // `initializeSubscriber` on top of it. The ladder's timer ref is null for
      // the whole duration of an attempt, so it cannot serve as this guard.
      if (initInFlightRef.current) return;

      // Rate-limit, so a flapping network can't turn wake events into a hot
      // loop of init attempts.
      const now_0 = Date.now();
      if (now_0 - lastWakeRetryAtRef.current < WAKE_RETRY_MIN_INTERVAL_MS) return;
      lastWakeRetryAtRef.current = now_0;

      // Collapse a pending backoff rather than deferring to it: a wake means
      // the condition that caused the failure may have just cleared, and
      // waiting out a 60-second rung would leave the user unreachable for no
      // reason. Deliberately does NOT reset `initAttemptRef` — the ladder keeps
      // its place, so a flapping network cannot defeat backoff.
      if (initRetryTimerRef.current) {
        clearTimeout(initRetryTimerRef.current);
        initRetryTimerRef.current = null;
      }
      void recoverConnection("wake");
    };

    // Visibility and drift are gated on the tab being visible; `online` is
    // deliberately not. A backgrounded tab still has to receive calls, and the
    // network coming back is exactly when it needs to re-register — gating it
    // would strand precisely the long-lived hidden tab that most of the
    // production failures came from.
    const onVisibleWake = () => {
      if (document.visibilityState !== "visible") return;
      onWake();
    };
    const handleVisibilityChange = () => onVisibleWake();
    const handleOnline = () => onWake();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    // Clock-drift wake detector. macOS lid-close on a foreground tab produces
    // neither `visibilitychange` nor `online`, so those listeners miss it. A
    // paused-then-resumed `setInterval` is the only reliable cross-platform
    // signal that the device slept.
    //
    // Drift goes through `onVisibleWake` so background tabs throttled by
    // Chrome (1Hz, then 1/min after 5 minutes) don't generate a stream of
    // spurious wake checks. macOS lid-close on a foreground tab keeps
    // visibility "visible", so that case still passes.
    lastDriftTickAtRef.current = Date.now();
    const driftInterval = setInterval(() => {
      const now_1 = Date.now();
      const gap = now_1 - lastDriftTickAtRef.current;
      lastDriftTickAtRef.current = now_1;
      if (gap > DRIFT_WAKE_THRESHOLD_MS) {
        console.log(`[InboundCall] Drift wake detected, gap=${gap}ms`);
        onVisibleWake();
      }
    }, DRIFT_CHECK_INTERVAL_MS);
    driftIntervalRef.current = driftInterval;

    // Health check of the live client. A no-op while it is connected and
    // registered. Skipped with no client, so a user who denied the mic is not
    // re-prompted every minute.
    const healthInterval = setInterval(() => {
      if (clientRef.current) void recoverConnection("interval");
    }, HEALTH_CHECK_INTERVAL_MS);
    healthIntervalRef.current = healthInterval;
    const backstopInterval = setInterval(() => {
      void recoverConnection("interval");
    }, RECOVERY_BACKSTOP_INTERVAL_MS);
    return () => {
      // Set first: an init attempt may still be awaiting, and its catch would
      // otherwise re-arm the ladder on a provider that no longer exists.
      disposedRef.current = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      clearInterval(driftInterval);
      clearInterval(healthInterval);
      clearInterval(backstopInterval);
      driftIntervalRef.current = null;
      healthIntervalRef.current = null;
      lastDriftTickAtRef.current = 0;
      if (initRetryTimerRef.current) {
        clearTimeout(initRetryTimerRef.current);
        initRetryTimerRef.current = null;
      }
      initAttemptRef.current = 0;
      micDeniedRef.current = false;
    };
  }, [currentUser?.isRingGroupMember, recoverConnection]);
  useEffect(() => {
    if (currentUser?.isRingGroupMember) {
      void attemptInit();
    }
    return () => {
      cleanup();
    };
  }, [currentUser?.isRingGroupMember]);

  // --- call actions (match swdialer's answerCall/rejectCall/endCall) ---

  const answer = useCallback(async () => {
    const call_1 = pendingCallRef.current;
    if (!call_1) return;
    const answerStartedAt = Date.now();
    try {
      pendingCallRef.current = null;
      currentCallRef.current = call_1;
      setCurrentCall(call_1);
      // Publish to the shared store so the outbound dialer and any future
      // surface can see that this user is occupied.
      beginActiveCall(call_1.id, "inbound");
      setCallState("connecting");

      // Wait for the call to actually connect before anything treats it as
      // answered. `call.answer()` returns immediately; the answer reaches
      // SignalWire in the background. This used to show the call as active
      // and tell the backend straight away, so when SignalWire never replied
      // (the SDK gives the answer RPC 5s, and slow or VPN-routed connections
      // miss that) the staff member sat in a silent "connected" call, the leg
      // was stamped as answered by them, and SignalWire — having never
      // bridged it — rang out and handed the caller to the AI failover, which
      // could not link to a leg that claimed a human answer.
      call_1.answer();
      await waitForAnsweredCallToConnect(call_1);
      captureSoftphoneEvent("softphone_answered", {
        duration_ms: Date.now() - answerStartedAt
      });

      // Subscribe to call status (matches swdialer's setupCallSubscriptions)
      callStatusSubRef.current = call_1.status$.subscribe(status_0 => {
        if (status_0 === "disconnected" || status_0 === "failed") {
          if (currentCallRef.current === call_1) {
            handleCallEnded();
          }
        }
      });
      setCallState("active");
      if (matchedPatientRef.current) {
        openSidebar(patientSidebarDefinition, {
          patientTiny: matchedPatientRef.current
        });
      }
      notifyRingGroupAnswered().then(response => {
        if (response?.callId) {
          setActiveCallId(response.callId);
        }
      }).catch(() => {});
    } catch (error_8) {
      console.error("[InboundCall] Failed to answer call:", error_8);
      const errorName = (error_8 as Error | undefined)?.name ?? null;
      captureSoftphoneEvent("softphone_answer_failed", {
        duration_ms: Date.now() - answerStartedAt,
        error_name: errorName,
        error_message: (error_8 as Error | undefined)?.message ?? null
      });
      // Drop our end of the leg. A reply that arrives after the SDK gave up
      // could otherwise still bridge the caller to this browser, with the UI
      // already back to idle and nothing on screen to hang up with.
      call_1.hangup().catch(() => {});
      toast({
        title: "Call Error",
        description: errorName === "NotAllowedError" ? "Microphone access was denied. Please enable microphone access in your browser settings." : "The call couldn't be connected, so it wasn't answered. Please try again.",
        variant: "error"
      });
      endActiveCall(call_1.id);
      currentCallRef.current = null;
      setCurrentCall(null);
      setCallState("idle");
      setCallerInfo(null);
      pendingCallRef.current = null;
    }
  }, [handleCallEnded, openSidebar, beginActiveCall, endActiveCall]);
  const decline = useCallback(() => {
    const call_2 = pendingCallRef.current;
    if (call_2) {
      try {
        call_2.reject();
      } catch {
        // ignore
      }
    }
    pendingCallRef.current = null;
    setCallState("idle");
    setCallerInfo(null);
  }, []);

  // Matches swdialer's endCall: eagerly clean up, then best-effort hangup
  const hangup = useCallback(() => {
    const call_3 = currentCallRef.current;
    handleCallEnded();
    if (call_3) {
      call_3.hangup().catch(() => {});
    }
  }, [handleCallEnded]);
  const onTransferInitiated = useCallback(() => {
    setIsTransferring(true);
    setTimeout(() => {
      const call_4 = currentCallRef.current;
      handleCallEnded();
      if (call_4) {
        call_4.hangup().catch(() => {});
      }
    }, 500);
  }, [handleCallEnded]);
  const value: InboundCallContextType = {
    callState,
    cancelReason,
    callerInfo,
    matchedPatient,
    isPatientLookupLoading,
    dialedLocationName,
    answer,
    decline,
    hangup,
    toggleMute,
    isMuted,
    currentCall,
    activeCallId,
    isTransferring,
    onTransferInitiated
  };
  return <InboundCallContext.Provider value={value}>
      {children}
    </InboundCallContext.Provider>;
};